import React from 'react';
import { Checkbox } from '@insurancemenu/imenu-components-lib';
import { observer } from 'mobx-react-lite';
import { useGenerateProposalPageStore } from 'store/pages/proposal-generation/generateProposalPageStore';
import ProposalGenerationStepHeader from '../ProposalGenerationStepHeader';
import {
  ContentWrapperStyled,
  RadioGroupStyled,
  StepSectionStyled,
  StepBottomSectionStyled,
  ToggleStyled,
} from './styles';
import {
  FIRST_STEP_TEST_ID,
  INCLUDE_BC_CHECKBOX,
  INCLUDE_BC_OPTION_TEST_ID,
  INCLUDE_PLAN_DETAILS_OPTION_TEST_ID,
  INCLUDE_PLAN_HIGHLIGHTS_CHECKBOX,
  INCLUDE_PRODUCT_LOGO_OPTION_TEST_ID,
  INCLUDE_RATE_SHEET_BC_OPTION_TEST_ID,
  INCLUDE_RATE_SHEET_OPTION_TEST_ID,
  INCLUDE_RATE_SHEET_PLAN_HIGHLIGHTS_OPTION_TEST_ID,
  INCLUDE_RATES_BC_CHECKBOX,
  INCLUDE_RATES_BLOCK_OPTION_TEST_ID,
  INCLUDE_RATES_CHECKBOX,
  SHOW_ALL_PRODUCT_ATTRIBUTES_OPTION,
  SHOW_ALL_PRODUCT_ATTRIBUTES_OPTION_TEST_ID,
  SHOW_ONLY_PLAN_HIGHLIGHTS_OPTION,
  SHOW_ONLY_PLAN_HIGHLIGHTS_OPTION_TEST_ID,
} from '../constants';

const planDetailsToggleLabel = 'Include Plan Details';
const rateSheetToggleLabel = 'Include Rate Sheet (PDF Only)';
const includeProductLogoToggleLabel = 'Include Product Logo';

const radioButtons = [
  {
    label: 'Show all',
    value: 'all',
    id: SHOW_ALL_PRODUCT_ATTRIBUTES_OPTION,
    key: SHOW_ALL_PRODUCT_ATTRIBUTES_OPTION,
    'data-test-id': SHOW_ALL_PRODUCT_ATTRIBUTES_OPTION_TEST_ID,
  },
  {
    label: 'Show only Plan Highlights',
    value: 'plan-highlights-only',
    id: SHOW_ONLY_PLAN_HIGHLIGHTS_OPTION,
    key: SHOW_ONLY_PLAN_HIGHLIGHTS_OPTION,
    'data-test-id': SHOW_ONLY_PLAN_HIGHLIGHTS_OPTION_TEST_ID,
  },
];

const FirstStep: React.FC = observer(() => {
  const {
    firstStepController: { form },
  } = useGenerateProposalPageStore();

  function onRadioGroupChange(e: React.ChangeEvent<HTMLInputElement>): void {
    form.onRadioGroupChange(e, 'productAttributesSetting');
  }

  return (
    <section data-test-id={FIRST_STEP_TEST_ID}>
      <ProposalGenerationStepHeader
        title="Select Proposal Components"
        info="Choose which components to include in your proposal. Each option comes with more details that you can fine-tune to fit your needs."
      />
      <StepSectionStyled>
        <ToggleStyled
          name="includeRateSheet"
          labelActive={rateSheetToggleLabel}
          labelInactive={rateSheetToggleLabel}
          fixedLabelSide="right"
          checked={form.fields.includeRateSheet.value === true}
          onChange={form.onCheckboxChange}
          data-test-id={INCLUDE_RATE_SHEET_OPTION_TEST_ID}
        />
        {form.fields.includeRateSheet.value === true ? (
          <ContentWrapperStyled>
            <Checkbox
              id={INCLUDE_RATES_BC_CHECKBOX}
              label="Include Browsing Criteria"
              name="includeRateSheetBC"
              checked={form.fields.includeRateSheetBC.value === true}
              onChange={form.onCheckboxChange}
              data-test-id={INCLUDE_RATE_SHEET_BC_OPTION_TEST_ID}
            />
            <Checkbox
              id={INCLUDE_PLAN_HIGHLIGHTS_CHECKBOX}
              name="includeRateSheetPlanHighlights"
              checked={form.fields.includeRateSheetPlanHighlights.value === true}
              label="Include Plan Highlights"
              onChange={form.onCheckboxChange}
              data-test-id={INCLUDE_RATE_SHEET_PLAN_HIGHLIGHTS_OPTION_TEST_ID}
            />
          </ContentWrapperStyled>
        ) : null}
      </StepSectionStyled>
      <StepBottomSectionStyled>
        <ToggleStyled
          checked={form.fields.includePlanDetails.value === true}
          name="includePlanDetails"
          labelActive={planDetailsToggleLabel}
          labelInactive={planDetailsToggleLabel}
          fixedLabelSide="right"
          onChange={form.onCheckboxChange}
          data-test-id={INCLUDE_PLAN_DETAILS_OPTION_TEST_ID}
        />
        {form.fields.includePlanDetails.value === true ? (
          <ContentWrapperStyled>
            <Checkbox
              id={INCLUDE_RATES_CHECKBOX}
              label="Include Rates Block"
              name="includeRatesBlock"
              checked={form.fields.includeRatesBlock.value}
              onChange={form.onCheckboxChange}
              data-test-id={INCLUDE_RATES_BLOCK_OPTION_TEST_ID}
            />
            <Checkbox
              id={INCLUDE_BC_CHECKBOX}
              label="Include Browsing Criteria"
              name="includeBC"
              checked={form.fields.includeBC.value}
              onChange={form.onCheckboxChange}
              data-test-id={INCLUDE_BC_OPTION_TEST_ID}
            />
            <RadioGroupStyled
              value={form.fields.productAttributesSetting.value}
              label="Product Attributes"
              onChange={onRadioGroupChange}
              radioButtons={radioButtons}
            />
          </ContentWrapperStyled>
        ) : null}
        <ToggleStyled
          name="includeProductLogo"
          labelActive={includeProductLogoToggleLabel}
          labelInactive={includeProductLogoToggleLabel}
          fixedLabelSide="right"
          checked={form.fields.includeProductLogo.value === true}
          onChange={form.onCheckboxChange}
          data-test-id={INCLUDE_PRODUCT_LOGO_OPTION_TEST_ID}
        />
      </StepBottomSectionStyled>
    </section>
  );
});

export default FirstStep;
